// FILE: src/services/addressSuggestions.js
// Google address suggestions for the address forms (getting started, My
// profile › Details). Optional: the browser key comes at runtime from
// GET /api/public/info → addressAutocomplete ({ provider: "google", apiKey }
// or null), so turning it on or off needs no rebuild. Without a key, or when
// Google can't be reached, the fields stay plain inputs with the browser's
// own address autofill (autocomplete tokens).
//
// Uses the Places API (New) through the Maps JavaScript API (the legacy
// google.maps.places.Autocomplete widget isn't offered to new customers):
// the script is added once, only when an address form asks for it, and
// importLibrary("places") gives AutocompleteSuggestion + AutocompleteSessionToken.
import { getPublicInfo } from "./courseInfo";
import { normalisePostalCode, provinceCode } from "../components/student/provinces";

export const MAPS_SCRIPT_URL = "https://maps.googleapis.com/maps/api/js";
const SCRIPT_ID = "zun-google-maps";
const LOAD_TIMEOUT_MS = 15000;

let configPromise = null;
let libraryPromise = null;
let library = null;
let unavailable = false;
const listeners = new Set();

/** The address-suggestion config from /api/public/info, or null (never rejects). */
export function getAddressAutocompleteConfig() {
  if (!configPromise) {
    configPromise = getPublicInfo()
      .then((info) => {
        const cfg = info && info.addressAutocomplete;
        return cfg && cfg.provider === "google" && typeof cfg.apiKey === "string" && cfg.apiKey ? cfg : null;
      })
      .catch(() => {
        configPromise = null; // try again on the next address form
        return null;
      });
  }
  return configPromise;
}

/** True once Google refused us (bad key, API not enabled…): plain fields for the rest of the visit. */
export function suggestionsUnavailable() {
  return unavailable;
}

export function markSuggestionsUnavailable() {
  if (unavailable) return;
  unavailable = true;
  for (const fn of [...listeners]) fn();
}

/** Calls `fn` when suggestions become unavailable; returns the unsubscribe function. */
export function onSuggestionsUnavailable(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The places library if it has already loaded, else null. */
export function loadedPlacesLibrary() {
  return unavailable ? null : library;
}

function injectScript(apiKey) {
  return new Promise((resolve, reject) => {
    // Google calls this global when the key is refused (referrer, API not enabled…).
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => {
      markSuggestionsUnavailable();
      if (typeof previous === "function") previous();
    };
    const params = new URLSearchParams({ key: apiKey, v: "weekly", libraries: "places", loading: "async" });
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `${MAPS_SCRIPT_URL}?${params.toString()}`;
    script.async = true;
    const timer = setTimeout(() => reject(new Error("Google Maps script timed out")), LOAD_TIMEOUT_MS);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      script.remove();
      reject(new Error("Google Maps script failed to load"));
    };
    document.head.appendChild(script);
  });
}

/**
 * Loads the Maps JavaScript API once and resolves to
 * { AutocompleteSuggestion, AutocompleteSessionToken }. Rejects when the
 * script or the library can't be loaded (a later call tries again).
 */
export function loadPlacesLibrary(apiKey) {
  if (unavailable) return Promise.reject(new Error("Address suggestions unavailable"));
  if (!libraryPromise) {
    const hasApi = () => typeof window.google?.maps?.importLibrary === "function";
    libraryPromise = (hasApi() ? Promise.resolve() : injectScript(apiKey))
      .then(() => {
        if (!hasApi()) throw new Error("Google Maps importLibrary missing");
        return window.google.maps.importLibrary("places");
      })
      .then((places) => {
        if (!places?.AutocompleteSuggestion || !places?.AutocompleteSessionToken) {
          throw new Error("Places library has no AutocompleteSuggestion");
        }
        library = {
          AutocompleteSuggestion: places.AutocompleteSuggestion,
          AutocompleteSessionToken: places.AutocompleteSessionToken
        };
        return library;
      })
      .catch((err) => {
        libraryPromise = null;
        throw err;
      });
  }
  return libraryPromise;
}

// Place types for a street address (Places API (New) Table B).
export const ADDRESS_TYPES = ["street_address", "premise", "subpremise"];

/**
 * Up to 5 suggestions for `input`, Canada only:
 * [{ id, label, main, secondary, prediction }]. Asks for street addresses
 * first; when that finds nothing (some unit addresses), asks again without
 * the type filter.
 */
export async function fetchAddressSuggestions(places, input, sessionToken) {
  const base = { input, sessionToken, includedRegionCodes: ["ca"], language: "en-CA", region: "ca" };
  let { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
    ...base,
    includedPrimaryTypes: ADDRESS_TYPES
  });
  if (!suggestions || suggestions.length === 0) {
    ({ suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(base));
  }
  return (suggestions || [])
    .map((s) => s && s.placePrediction)
    .filter(Boolean)
    .slice(0, 5)
    .map((p, i) => {
      const label = String(p.text || "");
      return {
        id: p.placeId || `${i}:${label}`,
        label,
        main: String(p.mainText || label),
        secondary: p.secondaryText ? String(p.secondaryText) : "",
        prediction: p
      };
    });
}

function component(components, type, { short = false } = {}) {
  const hit = (components || []).find((c) => Array.isArray(c?.types) && c.types.includes(type));
  if (!hit) return "";
  return String((short ? hit.shortText || hit.longText : hit.longText || hit.shortText) || "").trim();
}

/**
 * Places (New) addressComponents → the profile's address fields
 * ({ street, city, province, postalCode }); a field Google didn't give is
 * left out. There is no separate unit field (the server keeps one street
 * line), so a unit goes in front, Canada Post style: "4-123 Main Street".
 */
export function addressFromComponents(components, fallbackStreet = "") {
  const number = component(components, "street_number");
  const route = component(components, "route");
  const unit = component(components, "subpremise");
  let street = [number, route].filter(Boolean).join(" ");
  if (street && unit) street = /^[A-Za-z0-9]{1,6}$/.test(unit) && number ? `${unit}-${street}` : `${street}, ${unit}`;
  if (!street) street = String(fallbackStreet || "").trim();

  const city =
    component(components, "locality") ||
    component(components, "postal_town") ||
    component(components, "sublocality_level_1") ||
    component(components, "administrative_area_level_3");
  const province =
    provinceCode(component(components, "administrative_area_level_1", { short: true })) ||
    provinceCode(component(components, "administrative_area_level_1"));
  const rawPostal = component(components, "postal_code");
  const postalCode = rawPostal ? normalisePostalCode(rawPostal) || rawPostal.toUpperCase() : "";

  const out = {};
  if (street) out.street = street.slice(0, 120);
  if (city) out.city = city.slice(0, 60);
  if (province) out.province = province;
  if (postalCode) out.postalCode = postalCode;
  return out;
}

/** The suggestion's place → address fields (ends the billing session). */
export async function addressForSuggestion(option) {
  const place = option.prediction.toPlace();
  await place.fetchFields({ fields: ["addressComponents"] });
  return addressFromComponents(place.addressComponents, option.main);
}

/** Tests only: forget the cached config, script and failure state. */
export function __resetAddressSuggestionsForTests() {
  configPromise = null;
  libraryPromise = null;
  library = null;
  unavailable = false;
  listeners.clear();
  document.getElementById(SCRIPT_ID)?.remove();
}
